import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, PhoneCall } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { mediaUrl } from "../lib/api";
import { external } from "../lib/links";

const lineVariants = {
  hidden: { opacity: 0, y: 44 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { content } = useContent();
  const h = content.hero;
  const links = content.links;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.16]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "34%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const headline = [h.line1, h.line2];

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-[56vh] md:min-h-[60vh] w-full overflow-hidden flex items-center">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={mediaUrl(h.image)}
          alt="Calm Boundary Bay at dusk near Tsawwassen, BC — the setting for Karen Boley Counselling"
          width="2400"
          height="1600"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#1c2b33]/60 via-[#1c2b33]/20 to-transparent" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#1c2b33]/55 via-transparent to-transparent" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative z-10 mx-auto max-w-7xl px-5 md:px-8 w-full pt-24 pb-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-white/85 mb-6"
        >
          {h.eyebrow}
        </motion.p>

        <h1 className="font-serif font-light text-white tracking-tight leading-[1.04] text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-5xl drop-shadow-[0_2px_20px_rgba(20,30,35,0.35)]">
          {headline.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block md:whitespace-nowrap">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a data-testid="hero-book-btn" href={links.janeBook} {...external} className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-8 py-4 font-semibold transition-[background-color,transform,box-shadow] duration-300 hover:bg-sage-dark hover:-translate-y-0.5 hover:shadow-xl">
            {h.ctaBook} <ArrowUpRight className="h-5 w-5" />
          </a>
          <a data-testid="hero-consult-btn" href={links.janeConsult} {...external} className="inline-flex items-center gap-2 rounded-full bg-white/12 backdrop-blur-md border border-white/45 text-white px-8 py-4 font-semibold transition-[background-color,transform] duration-300 hover:bg-white/22 hover:-translate-y-0.5">
            <PhoneCall className="h-4 w-4" /> {h.ctaConsult}
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
